import {useCallback, useMemo, useRef, useState} from 'react';
import {useReactToPrint} from 'react-to-print';
import AppPrintTable, {
  PrintColumn,
} from '../../../../../components/common/AppPrintTable';
import {SVG} from '../../../../../constants/assetLists';
import {exportToExcelFromColumns} from '../../../../../utils/exportToExcel';
import AppMenu from '../../../../../components/common/AppMenu';
import AppConfirmationDialog from '../../../../../components/common/AppConfirmationDialog';
import {useChecklistContext} from '../../../../../context/checklist/ChecklistContextProvider';
import {useClientsContext} from '../../../../../context/clients/ClientsContextProvider';
import {useOperationsContext} from '../../../../../context/OperationsContextProvider';
import {BranchLocationStatus, ClientLocationStatus, IChecklist} from '../../../checklist';
import {getChecklistLocationStatus} from '../../../checklistLocationUtils';

/**
 * Helper to create print columns with location-aware status
 */
const createChecklistColumns = (
  getLocationStatus: (checklist: IChecklist) => ClientLocationStatus | BranchLocationStatus | null,
  shiftTimeList: any,
): Array<PrintColumn<IChecklist>> => [
  {
    title: "שם צ'קליסט",
    accessor: r => r.title || '-',
    width: 180,
  },
  {
    title: 'מחלקה',
    accessor: r =>
      r.departments === 'all'
        ? 'כל המחלקות'
        : Array.isArray(r.departments)
          ? r.departments.map(d => d.name).join(', ')
          : '-',
    width: 200,
  },
  {
    title: 'שם',
    accessor: r =>
      `${r.createdBy?.firstName ?? ''} ${r.createdBy?.lastName ?? ''}`.trim() ||
      '-',
    width: 150,
  },
  {
    title: 'תיאור',
    accessor: r => r.description || '-',
    width: 200,
  },
  {
    title: 'משוייך ל',
    accessor: r => {
      const shifts = r.shiftTime || [];
      const totalShifts = shiftTimeList?.shifts?.length || 0;
      if (totalShifts > 0 && shifts.length === totalShifts) {
        return 'כל המשמרות';
      }
      return (
        shifts
          .map((s: any) => s?.name || '')
          .filter(Boolean)
          .join(', ') || '-'
      );
    },
    width: 150,
  },
  {
    title: '# משימות',
    accessor: r => String(r.list?.length || 0),
    width: 100,
  },
  {
    title: 'סטאטוס',
    accessor: r => {
      const status = getLocationStatus(r);
      const statusLabels: Record<string, string> = {
        active: 'פעיל',
        inactive: 'לא פעיל',
        cancelled: 'בוטל',
      };
      return statusLabels[status ?? 'active'] ?? 'פעיל';
    },
    width: 100,
  },
  {
    title: 'מוצגת בבריף',
    accessor: r => (r.showInBrief ? 'כן' : 'לא'),
    width: 120,
  },
  {
    title: 'חובת אישור',
    accessor: r => (r.approvalRequired ? 'כן' : 'לא'),
    width: 110,
  },
  {
    title: 'בית עסק',
    accessor: r => r.clientIds?.map(c => c.name).join(', ') || '-',
    width: 130,
  },
];
interface ChecklistTask {
  id: string;
  checklistName: string;
  taskDetails: string;
  status: string;
}

const TASK_COLUMNS: Array<PrintColumn<ChecklistTask>> = [
  {
    title: "צ'קליסט",
    accessor: r => r.checklistName,
    width: 200,
  },
  {
    title: 'משימה',
    accessor: r => r.taskDetails || '-',
    width: 300,
  },
  {
    title: 'סטאטוס',
    accessor: r => (r.status === 'active' ? 'פעיל' : 'לא פעיל'),
    width: 120,
  },
];

/**
 * Menu items – exactly like the image
 */
const MENUITEMS = [
  {
    title: "יצוא רשימה לאקסל",
    value: 'export',
    icon: SVG.csvGreenIcon,
  },
  {
    title: "הדפסת תוכן צ’קליסט",
    value: 'printContent',
    icon: SVG.printerBlackIcon,
  },
  {
    title: "הדפסת רשימת צ’קליסטים",
    value: 'printList',
    icon: SVG.printerBlackIcon,
  },
  {
    title: "הצגת ארכיון (מוסיף צ’קליסטים לא פעילים לטבלה)",
    value: 'archived',
    icon: 'checkbox',
  },
];

function ChecklistMenuSection() {
  const {selectedChecklists, flatChecklists, menuSelectedItem, setMenuSelectedItem} =
    useChecklistContext();
  const {selectedLocation} = useClientsContext();
  const {shiftTimeList} = useOperationsContext();

  const getLocationStatus = useCallback(
    (checklist: IChecklist): ClientLocationStatus | BranchLocationStatus | null =>
      getChecklistLocationStatus(checklist, selectedLocation),
    [selectedLocation],
  );

  const rawChecklists = flatChecklists;

  // Create columns with location-aware status accessor
  const checklistColumns = useMemo(
    () => createChecklistColumns(getLocationStatus, shiftTimeList),
    [getLocationStatus, shiftTimeList],
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printMode, setPrintMode] = useState<'content' | 'list'>('list');

  const printRef = useRef<HTMLDivElement>(null);

  const rowsToPrint = useMemo(() => {
    if (!rawChecklists) return [];
    return selectedChecklists.length > 0
      ? rawChecklists.filter(c => selectedChecklists.includes(c._id))
      : rawChecklists;
  }, [rawChecklists, selectedChecklists]);

  const taskRows: ChecklistTask[] = useMemo(() => {
    if (!rawChecklists) return [];

    const source =
      selectedChecklists.length > 0
        ? rawChecklists.filter(c => selectedChecklists.includes(c._id))
        : rawChecklists;

    return source.flatMap((checklist: IChecklist) =>
      (checklist.list || []).map((task, index: number) => ({
        id: `${checklist._id}-${index}`,
        checklistName: checklist.title ?? '-',
        taskDetails: task.details ?? '-',
        status: task.activeStatus === 'active' ? 'active' : 'inactive',
      })),
    );
  }, [rawChecklists, selectedChecklists]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'Checklists',
  });

  const handleItemClick = (val: string) => {
    if (val === 'export') {
      const clientName =
        rowsToPrint && rowsToPrint.length > 0 && rowsToPrint[0]?.clientIds
          ? rowsToPrint[0]?.clientIds
              ?.map(c => c.name)
              .join(' ')
              ?.replaceAll(' ', '_')
          : 'Checklists';
      const branchName =
        selectedLocation && !selectedLocation?.isClient
          ? selectedLocation?.name?.replaceAll(' ', '_')
          : null;
      const fileName = branchName
        ? `Checklists_${clientName}_${branchName}.xlsx`
        : `Checklists_${clientName}.xlsx`;
      exportToExcelFromColumns(checklistColumns, rowsToPrint ?? [], fileName);
    } else if (val === 'printContent') {
      setPrintMode('content');
      setShowPrintDialog(true);
    } else if (val === 'printList') {
      setPrintMode('list');
      setShowPrintDialog(true);
    } else if (val === 'archived') {
      setMenuSelectedItem(prev => {
        if (prev === 'archived') {
          return '';
        } else {
          return val;
        }
      });
    }

    setAnchorEl(null);
  };

  return (
    <>
      <AppMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onOpen={e => setAnchorEl(e.currentTarget as HTMLElement)}
        onClose={() => setAnchorEl(null)}
        items={MENUITEMS}
        showDivider
        dividerIndex={2}
        itemIconWidth={24}
        itemIconHeight={24}
        selectedItem={menuSelectedItem}
        onItemClick={handleItemClick}
      />

      <div style={{display: 'none'}}>
        {printMode === 'list' ? (
          <AppPrintTable<IChecklist>
            ref={printRef}
            title="רשימת צ'קליסטים"
            subtitle={
              selectedChecklists.length > 0
                ? `מספר צ'קליסטים מסומנים: ${selectedChecklists.length}`
                : `סה״כ צ'קליסטים: ${rowsToPrint.length}`
            }
            columns={checklistColumns}
            rows={rowsToPrint}
          />
        ) : (
          <AppPrintTable<ChecklistTask>
            ref={printRef}
            title="משימות צ'קליסט"
            subtitle={
              selectedChecklists.length > 0
                ? `מספר משימות מסומנות: ${taskRows.length}`
                : `סה״כ משימות: ${taskRows.length}`
            }
            columns={TASK_COLUMNS}
            rows={taskRows}
          />
        )}
      </div>

      <AppConfirmationDialog
        open={showPrintDialog}
        onClose={() => setShowPrintDialog(false)}
        onConfirm={() => {
          setShowPrintDialog(false);
          handlePrint();
        }}
        icon={
          <img
            src={SVG.printerAquaLgIcon}
            alt="icon"
            className="w-[48px] h-[48px] object-contain"
          />
        }
        title="הדפסה"
        subTitle="שליחה להדפסה?"
        confirmBtnText="כן"
        cancelBtnText="לא"
      />
    </>
  );
}

export default ChecklistMenuSection;
